import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wand2, Loader2, CheckCircle2, AlertTriangle, Scissors } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CopyChecker = ({ value, onChange, title = "AI COPY CHECKER" }) => {
    const [internal, setInternal] = useState("");
    const [hasLogo, setHasLogo] = useState(false);
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);

    const message = onChange ? value ?? "" : internal;
    const update = (v) => {
        if (onChange) onChange(v);
        else setInternal(v);
        setResult(null);
    };

    const words = message.trim() ? message.trim().split(/\s+/).filter(Boolean).length : 0;
    const over = words > 7;

    const check = async () => {
        if (!message.trim()) {
            toast.error("Type your main billboard message first.");
            return;
        }
        setChecking(true);
        setResult(null);
        try {
            const res = await axios.post(`${API}/copy-check`, { message, has_logo: hasLogo });
            setResult(res.data);
        } catch (e) {
            toast.error("Copy checker is unavailable right now. Your word count is still shown above.");
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="rounded-2xl border border-gold/40 bg-noir/70 p-5 sm:p-7 shadow-[0_0_40px_rgba(255,255,255,0.1)]" data-testid="copy-checker">
            <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-hotpink to-hotpink-hover shadow-[0_0_18px_rgba(255,255,255,0.3)]">
                    <Wand2 size={18} className="text-noir" />
                </span>
                <div>
                    <p className="font-alt text-xs font-bold tracking-[0.22em] text-white">{title}</p>
                    <p className="text-[11px] text-zinc-500">Type your main billboard message — 7 words or less, plus your logo.</p>
                </div>
            </div>

            <textarea
                rows={2}
                className={`input-luxury mt-5 ${over ? "input-error" : ""}`}
                placeholder="e.g. LUXURY FACIALS. BOOK YOUR GLOW TODAY."
                value={message}
                onChange={(e) => update(e.target.value)}
                data-testid="copy-checker-input"
            />

            <div className="mt-3.5 flex flex-wrap items-center gap-3">
                <span
                    className={`rounded-full border px-3.5 py-1.5 font-alt text-[11px] font-bold tracking-[0.14em] ${
                        words === 0
                            ? "border-white/15 text-zinc-500"
                            : over
                                ? "border-rose-500/70 bg-rose-500/10 text-rose-400"
                                : "border-emerald-500/60 bg-emerald-500/10 text-emerald-400"
                    }`}
                    data-testid="copy-checker-count"
                >
                    {words} / 7 WORDS
                </span>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-300" data-testid="copy-checker-logo-toggle">
                    <input type="checkbox" className="h-4 w-4 accent-[#FFFFFF] cursor-pointer" checked={hasLogo} onChange={(e) => setHasLogo(e.target.checked)} />
                    My artwork includes my logo
                </label>
                <button
                    type="button"
                    onClick={check}
                    disabled={checking}
                    className="btn-glossy-pink ml-auto !px-5 !py-2 !text-[10px] disabled:opacity-60"
                    data-testid="copy-checker-button"
                >
                    {checking ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                    {checking ? "CHECKING…" : "CHECK MY MESSAGE"}
                </button>
            </div>

            {over && (
                <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-rose-400" role="alert" data-testid="copy-checker-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    Your main advertising message must contain NO MORE THAN 7 WORDS. Trim {words - 7} {words - 7 === 1 ? "word" : "words"} to comply.
                </p>
            )}

            {result && !result.error && (
                <div
                    className={`mt-4 rounded-xl border p-4 ${result.within_limit && result.has_logo ? "border-emerald-500/50 bg-emerald-500/[0.07]" : "border-hotpink/50 bg-hotpink/[0.07]"}`}
                    data-testid="copy-checker-result"
                >
                    <p className={`flex items-center gap-2 font-alt text-xs font-bold tracking-[0.18em] ${result.within_limit && result.has_logo ? "text-emerald-400" : "text-hotpink"}`}>
                        {result.within_limit && result.has_logo ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                        {result.within_limit && result.has_logo ? "SPOTLIGHT READY" : "NEEDS ATTENTION"}
                    </p>
                    {result.feedback && <p className="mt-2.5 text-[13px] leading-relaxed text-zinc-300">{result.feedback}</p>}
                    {result.suggestion && (
                        <div className="mt-3 rounded-lg border border-gold/35 bg-gold/[0.08] px-4 py-3">
                            <p className="flex items-center gap-1.5 font-alt text-[10px] font-bold tracking-[0.22em] text-gold-light">
                                <Scissors size={11} /> SUGGESTED 7-WORD VERSION
                            </p>
                            <p className="mt-1.5 font-display text-lg italic text-white" data-testid="copy-checker-suggestion">“{result.suggestion}”</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
